// Privacy filters for Sentry error and transaction events and breadcrumbs (used in
// plugins/Sentry.js as beforeSend, beforeSendTransaction, beforeBreadcrumb and an event
// processor). Replay events have their own filters (utils/sentry-replay-privacy.js).
//
// What this does:
// - User: keeps only the user ID, and tells Sentry not to infer the IP address.
// - URLs: removes query strings and fragments, and hides the reset-password token, in the
//   page URL, Referer, transaction names, spans, breadcrumbs, tags and stack frames.
// - Console: drops console breadcrumbs and console events that have only text.
//   Removes `extra` (console arguments and serialised promise rejection values).
// - Vue: removes component props. Keeps only known `contexts`.
// - Text (exception values, messages, tags, URLs): removes email addresses, URL query
//   strings and the values of element attributes.
// - If the filter fails, it sends a short event with no data from the original.
//
// It cannot find names or addresses in free text. See the PR for what stays.
//
// Tests: node --test tests/sentry-event-privacy.test.mjs

import { scrubElementDescription, scrubUrl } from './sentry-replay-privacy.js'

const FILTERED = '[Filtered]'

// The reset-password token in a text (path segment after /reset-password/).
const SECRET_PATH_IN_TEXT = /(\/reset-password\/)[^/?#\s"'<>]+/gi

// Email addresses, also URL-encoded once (`%40`) or twice (`%2540`). The domain must end
// in a label that starts with a letter, so `vue@3.4.21` stays. The lookbehind keeps the
// time linear on long words.
const EMAIL = /(?<![\w.+%-])[\w.+%-]+(?:@|%40|%2540)(?:[\w-]+\.)+[a-z][\w-]*/gi

// The query string and fragment of a URL or path in a text (`https://x/y?a=b`, `/y#a`).
// They are removed up to the next quote or line end, because values can hold spaces.
const URL_QUERY_IN_TEXT = /((?<![\w+.-])[a-z][\w+.-]*:\/\/[^\s?#"'<>]*|(?<![^\s"'(=:,])\/[^\s?#"'<>]*)[?#][^\n"'<>]*/gi

// A query string after any other text: `cases?search=x`, `?filters[name]=x`.
const BARE_QUERY_IN_TEXT = /\?[\w[\]%.-]+=[^\n"'<>]*/g

const ABSOLUTE_URL = /^[a-z][\w+.-]*:\/\//i

// Sentry's text for a promise rejected with a value that is not an Error.
const PRIMITIVE_REJECTION = /^(Non-Error promise rejection captured with value: )[\s\S]*$/

// Vue Router span data: `params.<name>` and `query.<name>` hold route values.
// Route paths hold only IDs (`[id]`) and the reset token (`[token]`), so `params.id` stays.
const ROUTE_VALUE_KEY = /^(params|query)\./
const KEPT_ROUTE_VALUE_KEYS = ['params.id']

// Span data keys that hold a URL, and keys that hold only a query string or fragment.
const SPAN_URL_KEYS = ['url', 'http.url', 'previous']
const SPAN_DROPPED_KEYS = ['http.query', 'http.fragment']

// Web Vitals tags that hold an element description (`div#app > img[alt="..."]`).
const ELEMENT_TAG_KEY = /^(lcp\.element|cls\.source\.\d+)$/

// Breadcrumb data that stays, by category. Other data is removed.
const BREADCRUMB_DATA_KEYS = {
  navigation: ['from', 'to'],
  xhr: ['method', 'url', 'status_code'],
  fetch: ['method', 'url', 'status_code'],
  ui: ['ui.component_name'],
}
const BREADCRUMB_URL_KEYS = ['from', 'to', 'url']

// Request headers that stay. Sentry adds only these two in the browser.
const REQUEST_HEADERS = ['User-Agent', 'Referer']

// Contexts that stay. `api_request` is added by this filter. Others are removed, so a
// later setContext() or integration cannot add data without a change here.
const KEPT_CONTEXTS = ['trace', 'vue', 'browser', 'os', 'device', 'replay', 'api_request']

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function isPrimitive(value) {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value)
}

/**
 * Remove email addresses, URL query strings and fragments, and the reset-password token
 * from a text. Values that are not strings are returned unchanged.
 */
export function scrubText(value) {
  if (typeof value !== 'string') {
    return value
  }

  return value
    .replace(SECRET_PATH_IN_TEXT, `$1${FILTERED}`)
    .replace(URL_QUERY_IN_TEXT, '$1')
    .replace(BARE_QUERY_IN_TEXT, '')
    .replace(EMAIL, FILTERED)
}

// For URL fields: no query string or fragment, no token, no email address in the path.
function cleanUrl(value) {
  return scrubText(scrubUrl(value))
}

// For element descriptions: attribute values removed. IDs and classes (`div#app`) stay.
function cleanElement(value) {
  return scrubText(scrubElementDescription(value))
}

// For texts that can be a URL (span descriptions, transaction names, other tags).
// It removes everything after the first `?` or `#`, so it can remove more than needed.
function cleanDescription(value) {
  return cleanUrl(scrubElementDescription(value))
}

// Keeps only primitive values. Strings are cleaned with `clean`.
function cleanPrimitives(object, clean) {
  const kept = Object.entries(object).filter(([, value]) => isPrimitive(value))
  return Object.fromEntries(kept.map(([key, value]) => [key, clean(value, key)]))
}

function scrubTags(tags) {
  if (!isPlainObject(tags)) {
    return undefined
  }

  return cleanPrimitives(tags, (value, key) => (ELEMENT_TAG_KEY.test(key) ? cleanElement(value) : cleanDescription(value)))
}

function scrubSpanData(data) {
  if (!isPlainObject(data)) {
    return undefined
  }

  const kept = Object.fromEntries(
    Object.entries(data).filter(
      ([key]) => !SPAN_DROPPED_KEYS.includes(key) && (!ROUTE_VALUE_KEY.test(key) || KEPT_ROUTE_VALUE_KEYS.includes(key)),
    ),
  )

  return cleanPrimitives(kept, (value, key) => (SPAN_URL_KEYS.includes(key) ? cleanUrl(value) : scrubText(value)))
}

// Spans in transactions, and the trace context of errors and transactions.
function scrubSpan(span) {
  // In SDK v7, transaction spans are Span objects. They link to the transaction and to
  // every other span, and Sentry sends only their toJSON() output. A copy of the object
  // would send all of it, so use the JSON form.
  const json = span && typeof span.toJSON === 'function' ? span.toJSON() : span

  if (!isPlainObject(json)) {
    return json
  }

  const scrubbed = { ...json }

  if ('description' in scrubbed) {
    // `ui.*` spans (interactions, Vue components) describe an element or a component.
    const isUi = typeof scrubbed.op === 'string' && scrubbed.op.startsWith('ui.')
    scrubbed.description = isUi ? cleanElement(scrubbed.description) : cleanDescription(scrubbed.description)
  }

  for (const [key, scrub] of [
    ['data', scrubSpanData],
    ['tags', scrubTags],
  ]) {
    if (key in scrubbed) {
      const value = scrub(scrubbed[key])
      if (value) scrubbed[key] = value
      else delete scrubbed[key]
    }
  }

  return scrubbed
}

function scrubBreadcrumbData(category, data) {
  const group = category.startsWith('ui.') ? 'ui' : category
  const keys = Object.hasOwn(BREADCRUMB_DATA_KEYS, group) ? BREADCRUMB_DATA_KEYS[group] : []

  if (!isPlainObject(data)) {
    return undefined
  }

  const kept = keys
    .filter((key) => key in data && isPrimitive(data[key]))
    .map((key) => [key, BREADCRUMB_URL_KEYS.includes(key) ? cleanUrl(data[key]) : data[key]])

  return kept.length ? Object.fromEntries(kept) : undefined
}

/**
 * Sentry `beforeBreadcrumb` callback, also used for the breadcrumbs in an event.
 * Returns null to drop the breadcrumb.
 */
export function scrubBreadcrumb(breadcrumb) {
  if (!isPlainObject(breadcrumb)) {
    return breadcrumb
  }

  const category = typeof breadcrumb.category === 'string' ? breadcrumb.category : ''

  // Console arguments can hold API errors with request headers and bodies.
  if (category === 'console') {
    return null
  }

  const scrubbed = { ...breadcrumb }

  if (typeof scrubbed.message === 'string') {
    // DOM breadcrumbs (ui.click, ui.input, ...) describe the element with its attributes.
    scrubbed.message = category.startsWith('ui.') ? cleanElement(scrubbed.message) : scrubText(scrubbed.message)
  }

  if ('data' in scrubbed) {
    const data = scrubBreadcrumbData(category, scrubbed.data)
    if (data) scrubbed.data = data
    else delete scrubbed.data
  }

  return scrubbed
}

function scrubUser(user) {
  return isPlainObject(user) && user.id !== undefined && user.id !== null ? { id: user.id } : undefined
}

function scrubRequest(request) {
  if (!isPlainObject(request)) {
    return undefined
  }

  // Only the URL and two headers stay. Query string, cookies, body and env are removed.
  const scrubbed = {}

  if (typeof request.url === 'string') scrubbed.url = cleanUrl(request.url)

  if (isPlainObject(request.headers)) {
    const headers = REQUEST_HEADERS.filter((name) => typeof request.headers[name] === 'string').map((name) => [
      name,
      name === 'Referer' ? cleanUrl(request.headers[name]) : request.headers[name],
    ])
    scrubbed.headers = Object.fromEntries(headers)
  }

  return scrubbed
}

function scrubFrame(frame) {
  // For inline scripts, the file name is the page URL.
  if (!isPlainObject(frame)) return frame
  const scrubbed = { ...frame }
  if ('filename' in scrubbed) scrubbed.filename = cleanUrl(scrubbed.filename)
  if ('abs_path' in scrubbed) scrubbed.abs_path = cleanUrl(scrubbed.abs_path)
  return scrubbed
}

function scrubException(exception) {
  if (!isPlainObject(exception)) {
    return exception
  }

  const scrubbed = { ...exception }

  // API errors and Vue errors can include URLs or text from the API response.
  // A promise rejected with a string or other value: the value can be any text.
  if (typeof scrubbed.value === 'string') {
    scrubbed.value = scrubText(scrubbed.value).replace(PRIMITIVE_REJECTION, `$1${FILTERED}`)
  }

  if (Array.isArray(scrubbed.stacktrace?.frames)) {
    scrubbed.stacktrace = { ...scrubbed.stacktrace, frames: scrubbed.stacktrace.frames.map(scrubFrame) }
  }

  if (isPlainObject(scrubbed.mechanism?.data)) {
    scrubbed.mechanism = { ...scrubbed.mechanism, data: cleanPrimitives(scrubbed.mechanism.data, scrubText) }
  }

  return scrubbed
}

function scrubContexts(contexts) {
  if (!isPlainObject(contexts)) {
    return undefined
  }

  const scrubbed = Object.fromEntries(Object.entries(contexts).filter(([key]) => KEPT_CONTEXTS.includes(key)))

  if ('trace' in scrubbed) scrubbed.trace = scrubSpan(scrubbed.trace)

  if (isPlainObject(scrubbed.vue)) {
    // Component props can hold any data. Component names and the hook stay.
    const { propsData, ...vue } = scrubbed.vue // eslint-disable-line no-unused-vars
    scrubbed.vue = vue
  }

  return scrubbed
}

/**
 * Method, URL path and status of a failed Axios request, with no query string, headers
 * or bodies. This replaces the request URL that the removed console arguments held.
 */
export function describeApiRequest(error) {
  if (!error || error.isAxiosError !== true) {
    return null
  }

  const config = isPlainObject(error.config) ? error.config : {}
  const { url, baseURL } = config
  const isRelative = typeof url === 'string' && !ABSOLUTE_URL.test(url)
  const fullUrl = isRelative && typeof baseURL === 'string' && baseURL ? `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}` : url

  const request = {
    method: typeof config.method === 'string' ? config.method.toUpperCase() : undefined,
    url: typeof fullUrl === 'string' ? cleanUrl(fullUrl) : undefined,
    status_code: typeof error.response?.status === 'number' ? error.response.status : undefined,
    code: typeof error.code === 'string' ? error.code : undefined,
  }

  return Object.fromEntries(Object.entries(request).filter(([, value]) => value !== undefined))
}

function isConsoleTextEvent(event, hint) {
  // captureConsoleIntegration sends console text with captureMessage. With
  // attachStacktrace on, that event also has an exception whose value is the text.
  return event.logger === 'console' && (!event.exception || typeof hint?.originalException === 'string')
}

function scrubEventFields(event, hint) {
  if (!isPlainObject(event)) {
    return event
  }

  if (isConsoleTextEvent(event, hint)) {
    return null
  }

  const scrubbed = { ...event }

  // Sentry infers the IP address from the connection unless the SDK says not to.
  // SDK v7 does not send this setting, so it is set here.
  scrubbed.sdk = { ...scrubbed.sdk, settings: { ...scrubbed.sdk?.settings, infer_ip: 'never' } }

  // Console arguments and promise rejection values (for example form field values).
  // The app does not set its own `extra` data.
  delete scrubbed.extra

  for (const [key, scrub] of [
    ['user', scrubUser],
    ['request', scrubRequest],
    ['tags', scrubTags],
    ['contexts', scrubContexts],
  ]) {
    if (key in scrubbed) {
      const value = scrub(scrubbed[key])
      if (value) scrubbed[key] = value
      else delete scrubbed[key]
    }
  }

  if ('transaction' in scrubbed) scrubbed.transaction = cleanDescription(scrubbed.transaction)
  if ('message' in scrubbed) scrubbed.message = scrubText(scrubbed.message)
  if (isPlainObject(scrubbed.logentry)) scrubbed.logentry = { message: scrubText(scrubbed.logentry.message) }
  if (Array.isArray(scrubbed.fingerprint)) scrubbed.fingerprint = scrubbed.fingerprint.map(scrubText)

  if (Array.isArray(scrubbed.exception?.values)) {
    scrubbed.exception = { ...scrubbed.exception, values: scrubbed.exception.values.map(scrubException) }
  }

  if (Array.isArray(scrubbed.spans)) scrubbed.spans = scrubbed.spans.map(scrubSpan)

  if (Array.isArray(scrubbed.breadcrumbs)) {
    scrubbed.breadcrumbs = scrubbed.breadcrumbs.map(scrubBreadcrumb).filter(Boolean)
  }

  const apiRequest = describeApiRequest(hint?.originalException)
  if (apiRequest) {
    scrubbed.contexts = { ...scrubbed.contexts, api_request: apiRequest }
  }

  return scrubbed
}

// When the filter fails, send only what cannot hold personal data, so the failure is
// visible in Sentry. Transactions and other event types are dropped.
function filterFailedEvent(event, error) {
  if (!isPlainObject(event) || event.type !== undefined) {
    return null
  }

  const kept = ['event_id', 'timestamp', 'platform', 'level', 'release', 'dist', 'environment']
  const safe = Object.fromEntries(kept.filter((key) => isPrimitive(event[key])).map((key) => [key, event[key]]))
  const errorName = error instanceof Error ? error.constructor.name : typeof error

  return {
    ...safe,
    message: `Sentry privacy filter failed (${errorName})`,
    tags: { privacy_filter: 'failed' },
    sdk: { name: event.sdk?.name, version: event.sdk?.version, settings: { infer_ip: 'never' } },
  }
}

/**
 * Sentry `beforeSend` and `beforeSendTransaction` filter. Returns a cleaned copy of the
 * event, or null to drop it. The input event is not changed.
 * It never throws: if Sentry's beforeSend throws, Sentry sends a new event about the
 * error without calling beforeSend again.
 */
export function scrubEvent(event, hint) {
  try {
    return scrubEventFields(event, hint)
  } catch (error) {
    return filterFailedEvent(event, error)
  }
}

/**
 * Sentry event processor. When any part of Sentry's event pipeline throws, Sentry sends
 * an event about that error and skips beforeSend for it. This cleans those events.
 * Other events are returned unchanged (beforeSend cleans them).
 */
export function scrubInternalEvent(event, hint) {
  return hint?.data?.__sentry__ === true ? scrubEvent(event, hint) : event
}
